import React, { useState } from "react";
import { Box, Grid, Typography, TextField, Divider } from "@mui/material";
import PlacesAutocompleteLib from "react-places-autocomplete";
import { colors } from "../../../../ui/colors";
import { Controller } from "react-hook-form";
import { SASInputStyles } from "../../../../ui/styles/inputStyles";

export const AddressInformationFields = ({ control, register, errors, setValue, getCoords = true }) => {
  const [isFocused, setIsFocused] = useState(false);

  const Label = ({ text }) => (
    <Typography
      variant="subtitle2"
      sx={{
        color: colors.gray[600],
        fontWeight: 600,
        mb: 1,
      }}
    >
      {text}
    </Typography>
  );

  const SectionTitle = ({ children, icon = true }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        {icon && (
          <Box
            sx={{
              width: 4,
              height: 24,
              bgcolor: colors.orange.base,
              borderRadius: 2,
              mr: 2,
            }}
          />
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.gray[800],
            letterSpacing: 0.5,
          }}
        >
          {children}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: colors.gray[200], opacity: 0.8 }} />
    </Box>
  );

  const handleSelect = (value, placeId) => {
    const service = new window.google.maps.places.PlacesService(document.createElement("div"));

    service.getDetails(
      {
        placeId,
        fields: ["address_components", "formatted_address", "geometry", "name"],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          console.warn("Not found");
          return;
        }

        const get = (type) => place.address_components.find((c) => c.types.includes(type))?.long_name || "";

        setValue("address", value, { shouldtouch: true, shouldValidate: true });
        setValue("city", get("locality") || get("sublocality") || "", { shouldtouch: true });
        setValue("state", get("administrative_area_level_1") || "", { shouldtouch: true });
        setValue("country", get("country") || "", { shouldtouch: true });
        setValue("zipCode", get("postal_code") || "", { shouldtouch: true });

        if (place.geometry?.location && getCoords) {
          setValue("latitude", place.geometry.location.lat());
          setValue("longitude", place.geometry.location.lng());
        }
      },
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <SectionTitle>Address Information</SectionTitle>

      <Grid container spacing={3}>
        {/* Campo de dirección con autocomplete */}
        <Grid item xs={12} md={6}>
          <Label text="Address" />
          <Controller
            name="address"
            control={control}
            rules={{ required: "Address is required" }}
            render={({ field }) => (
              <PlacesAutocompleteLib
                value={field.value || ""}
                onChange={(val) => field.onChange(val)}
                onSelect={(value, placeId) => {
                  field.onChange(value);
                  handleSelect(value, placeId);
                }}
              >
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                  <div style={{ position: "relative" }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter address"
                      {...getInputProps({
                        name: "address",
                        onFocus: () => setIsFocused(true),
                        onBlur: () => setIsFocused(false),
                      })}
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      sx={SASInputStyles}
                    />

                    {/* Sugerencias */}
                    <Box
                      sx={{
                        position: "absolute",
                        zIndex: 10,
                        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                        borderRadius: 1,
                        boxShadow: 2,
                        width: "100%",
                        mt: 0.5,
                        maxHeight: 220,
                        overflowY: "auto",
                      }}
                    >
                      {suggestions.map((s) => {
                        const style = {
                          padding: "8px 12px",
                          backgroundColor: s.active ? "var(--fx-surface-muted, #F1F5F9)" : "var(--fx-surface-base, #FFFFFF)",
                          cursor: "pointer",
                        };
                        return (
                          <div key={s.placeId} {...getSuggestionItemProps(s, { style })}>
                            {s.description}
                          </div>
                        );
                      })}
                    </Box>
                  </div>
                )}
              </PlacesAutocompleteLib>
            )}
          />
        </Grid>

        {/* Campos secundarios */}
        <Grid item xs={12} md={6}>
          <Label text="City" />
          <TextField
            fullWidth
            placeholder="City"
            {...register("city", { required: "City is required" })}
            error={!!errors.city}
            helperText={errors.city?.message}
            sx={SASInputStyles}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Label text="State / Province" />
          <TextField
            fullWidth
            placeholder="State"
            {...register("state", { required: "State is required" })}
            error={!!errors.state}
            helperText={errors.state?.message}
            sx={SASInputStyles}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Label text="Country" />
          <TextField
            fullWidth
            placeholder="Country"
            {...register("country", { required: "Country is required" })}
            error={!!errors.country}
            helperText={errors.country?.message}
            sx={SASInputStyles}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Label text="Zip Code" />
          <TextField
            fullWidth
            placeholder="Zip Code"
            {...register("zipCode", { required: "Zip code is required" })}
            error={!!errors.zipCode}
            helperText={errors.zipCode?.message}
            sx={SASInputStyles}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
